import { useEffect, useState } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";

export default function AdminSiteStatus() {
  const [active, setActive] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [previewToken, setPreviewToken] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/site-settings`)
      .then((res) => res.json())
      .then((json) => setActive(json.site_active !== false));
    fetch(`${API_BASE}/site-settings/preview`)
      .then((res) => res.json())
      .then((json) => setPreviewToken(json.token ?? ""));
  }, []);

  const previewLink = previewToken ? `${window.location.origin}${__BASE_PATH__}?preview=${previewToken}` : "";

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(previewLink);
      setPreviewMessage("Link copiado.");
    } catch {
      setPreviewMessage("No se pudo copiar automáticamente: selecciona el link y cópialo con Ctrl+C.");
    }
  }

  async function handleRegenerate() {
    if (!window.confirm("El link actual dejará de funcionar para quien lo tenga. ¿Generar uno nuevo?")) return;
    setRegenerating(true);
    setPreviewMessage(null);
    try {
      const res = await fetch(`${API_BASE}/site-settings/preview/regenerate`, { method: "POST" });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setPreviewMessage(json?.error || "No se pudo generar el link nuevo.");
        return;
      }
      setPreviewToken(json.token);
      setPreviewMessage("Link nuevo generado. El anterior ya no funciona.");
    } catch {
      setPreviewMessage("Error de conexión.");
    } finally {
      setRegenerating(false);
    }
  }

  async function handleToggle() {
    if (active === null) return;
    const next = !active;
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/site-settings/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ site_active: next }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setMessage({ type: "error", text: json?.error || "No se pudo guardar" });
        return;
      }
      setActive(json.site_active);
      setMessage({
        type: "success",
        text: next
          ? "El sitio quedó activo."
          : "El sitio quedó desactivado: los visitantes ven la página en construcción.",
      });
    } catch {
      setMessage({ type: "error", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  }

  if (active === null) {
    return (
      <AdminLayout>
        <p className="text-sm text-foreground-500">Cargando...</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Estado del sitio</h1>
      <p className="mt-1 text-sm text-foreground-500">
        Activa o desactiva la página pública. Cuando está desactivada, los visitantes ven un aviso de &quot;sitio en
        construcción&quot;. Este panel de administración sigue funcionando normal.
      </p>

      <div className="mt-6 max-w-lg rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex items-center gap-3">
          <span
            className={`w-3 h-3 rounded-full ${active ? "bg-primary-500" : "bg-accent-500"}`}
            aria-hidden="true"
          />
          <p className="font-heading font-semibold text-foreground-900">
            {active ? "Sitio activo" : "Sitio desactivado (en construcción)"}
          </p>
        </div>
        <p className="mt-2 text-sm text-foreground-500">
          {active
            ? "Los visitantes ven la página con normalidad."
            : "Los visitantes ven el aviso de que la página está en construcción y pronto estará activa."}
        </p>

        {message && (
          <div
            className={`mt-4 rounded-md p-3 text-sm border ${
              message.type === "success"
                ? "bg-primary-100 border-primary-200 text-primary-800"
                : "bg-accent-100 border-accent-200 text-accent-800"
            }`}
          >
            {message.text}
          </div>
        )}

        <button
          type="button"
          onClick={handleToggle}
          disabled={saving}
          className={`mt-5 rounded-md px-6 py-2.5 text-sm font-semibold text-background-50 disabled:opacity-60 cursor-pointer ${
            active ? "bg-accent-500 hover:bg-accent-600" : "bg-primary-500 hover:bg-primary-600"
          }`}
        >
          {saving ? "Guardando..." : active ? "Desactivar sitio" : "Activar sitio"}
        </button>
      </div>

      <div className="mt-6 max-w-lg rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="font-heading font-semibold text-foreground-900">Vista previa privada</h2>
        <p className="mt-1 text-sm text-foreground-500">
          Con este link puedes ver el sitio completo, con todos los cambios que hagas, aunque esté desactivado.
          Quien no tenga el link sigue viendo &quot;en construcción&quot;. Compártelo solo con quien quieras que lo vea.
        </p>

        <input
          readOnly
          value={previewLink}
          onFocus={(e) => e.currentTarget.select()}
          className="mt-4 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 text-xs text-foreground-700"
        />

        <div className="mt-3 flex flex-wrap gap-2">
          <a
            href={previewLink}
            target="_blank"
            rel="noreferrer"
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            Abrir vista previa
          </a>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!previewLink}
            className="rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60 cursor-pointer"
          >
            Copiar link
          </button>
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={regenerating}
            className="rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60 cursor-pointer"
          >
            {regenerating ? "Generando..." : "Generar link nuevo"}
          </button>
        </div>

        {previewMessage && <p className="mt-3 text-sm text-foreground-600">{previewMessage}</p>}
      </div>
    </AdminLayout>
  );
}
